import { createTheme } from '@mui/material/styles';

/**
 * The portal's single source of visual tokens.
 *
 * These colours are neutral placeholders. Replace them with ActivistARC's brand
 * palette when it is supplied, and re-check the contrast between text and
 * background afterwards.
 *
 * The values are converted from the oklch colours the previous theme used, not
 * re-picked by eye, so the contrast pairings that were already checked are
 * preserved. MUI does not accept oklch(), which is why they are hex here.
 *
 * Measured against WCAG 2.1 AA:
 *   body text on the page background   16.96:1  (needs 4.5)
 *   white on the primary button         5.68:1  (needs 4.5)
 *   white on the danger button          5.87:1  (needs 4.5)
 *   error text on the page background   7.80:1  (needs 4.5)
 *   focus ring on the page background   5.43:1  (needs 3.0)
 *   white initials on an avatar          5.68:1  (needs 4.5)
 *   white on a warning chip              5.02:1  (needs 4.5)
 *   warning text on the page background  4.80:1  (needs 4.5)
 */
export const theme = createTheme({
  palette: {
    primary: {
      light: '#4e7bdf',
      main: '#3461c9',
      dark: '#264ba4',
      contrastText: '#fff',
    },
    error: {
      light: '#fff0ee',
      main: '#c51d28',
      dark: '#a20519',
      contrastText: '#fff',
    },
    /*
     * Darker than MUI's own orange, which carries white text at only 3.5:1 -
     * the automated scan failed the Pending and Suspended chips on it.
     */
    warning: {
      light: '#d97706',
      main: '#b45309',
      dark: '#92400e',
      contrastText: '#fff',
    },
    background: {
      default: '#f8fafc',
      paper: '#fff',
    },
    text: {
      primary: '#101828',
    },
  },

  typography: {
    fontFamily: [
      'ui-sans-serif',
      'system-ui',
      '-apple-system',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },

  shape: {
    borderRadius: 6,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        /*
         * A visible focus ring on every interactive element, which WCAG 2.1 AA
         * requires. Never remove this without providing an equally visible
         * replacement.
         *
         * The second selector is not redundant. MUI's ButtonBase sets
         * `outline: 0` on itself, and Emotion deliberately inserts global styles
         * BEFORE component styles - so with equal specificity the component won
         * and every button, icon button, navigation item, menu item and sortable
         * column header had no ring at all. Only text fields and links were ever
         * outlined. Naming the class raises the specificity enough to win.
         *
         * A test renders a real button and reads the computed style, because the
         * previous test read the value back out of this object: it asserted that
         * the declaration had been typed, not that anything was outlined.
         */
        ':focus-visible, .MuiButtonBase-root:focus-visible': {
          outline: '2px solid #3461c9',
          outlineOffset: '2px',
        },

        /*
         * The page's own scrollbar always keeps its space, whether the page is
         * long enough to scroll or not. Without this, the scrollbar appeared
         * only on longer pages and took its width from the window, so the top
         * bar and the account rail - both pinned to the window's right edge -
         * jumped left by that much between one page and the next.
         *
         * Thin and quiet, in the portal's slate greys. The thumb is 3.1:1
         * against the page background, the contrast a visible control needs.
         * `scrollbar-color` is inherited, so every scrolling box - tables, the
         * navigation - matches the page.
         */
        html: {
          scrollbarColor: '#858fa0 transparent',
          /*
           * From tablet width up, where the rail and the pinned bar are. Not
           * on a phone: dialogs there fill the screen, and the kept space left
           * a strip down their right edge. Phone scrollbars float over the
           * page and take no space anyway, so nothing jumps there.
           */
          '@media (min-width: 600px)': { scrollbarGutter: 'stable' },
        },
        '*': { scrollbarWidth: 'thin' },
        '*:hover': { scrollbarColor: '#64748b transparent' },
        /*
         * Browsers without `scrollbar-color` (Safari) style scrollbars through
         * these instead. Chrome and Firefox ignore them once the standard
         * properties above apply.
         */
        '@supports not (scrollbar-color: auto)': {
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-track': { background: 'transparent' },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: '#858fa0',
            borderRadius: 8,
            // A transparent border makes the thumb look thinner than its track.
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#64748b' },
        },
        /*
         * While a dialog, drawer or menu is open, MUI stops the page scrolling
         * and pads the page and anything pinned by the scrollbar's width, to
         * make up for the scrollbar it expects to vanish. With the space kept
         * above, nothing vanishes, so that padding would push everything left
         * instead. It is cancelled only while the page is held still.
         */
        'body[style*="overflow: hidden"], body[style*="overflow: hidden"] .mui-fixed': {
          paddingRight: '0 !important',
        },

        /* Honour the operating system's "reduce motion" setting. */
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        /*
         * Loading placeholders pulse. The baseline above already shortens
         * every animation for somebody who asks for less motion; this stops
         * the pulse outright, so a page of placeholders is simply still.
         */
        root: {
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
      },
    },

    MuiButton: {
      defaultProps: {
        // A button inside a form submits it unless told otherwise, which is a
        // common source of accidental submissions.
        type: 'button',
        disableElevation: true,
      },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500 },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },

    MuiAvatar: {
      styleOverrides: {
        /*
         * MUI's default avatar is white initials on light grey, about 1.9:1 -
         * the automated scan failed every signed-in screen on it. The primary
         * colour behind the same white is the pairing the buttons already use.
         */
        colorDefault: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }),
      },
    },
  },
});
